import React, { useEffect, useState } from 'react';
import { Tabs, Tab, Container, Row, Col, Spinner } from 'react-bootstrap';
import { IoIosArrowForward } from 'react-icons/io';
import { IconContext } from 'react-icons';
import useAxios from 'axios-hooks';
import { useHistory } from 'react-router-dom';
import { useStateValue } from '../state';
import { gojump, gosky, hasGoSky, asList, mergeTrips, tripDetailsPath } from '../Api/trips';

function TripsList() {
  const [state] = useStateValue();
  const { locationId } = state;
  const roles = state.roles || [];
  const [LocationId, setLocationId] = useState(locationId);
  const [isOffline, setIsOffline] = useState(false);
  const [spinner, setSpinner] = useState(false);
  const [, dispatch] = useStateValue();

  useEffect(() => {
    setLocationId(locationId);
  }, [locationId]);

  const updateData = async (val) => {
    setLocationId(val);
    dispatch({ type: 'changeLocation', id: val });
  };

  const [{ date }] = useStateValue();
  const selectedDate = date.format('YYYY-MM-DD');
  const history = useHistory();

  const [{ data: rawGoJump, loading, error }, refetch] = useAxios(gojump.tripsUrl(selectedDate, LocationId), {
    useCache: false,
  });
  // Hawaii shares its bus with GoSky: their trips are merged into the same
  // list. Elsewhere the hook stays idle (manual, no URL).
  const withGoSky = hasGoSky(LocationId);
  const [{ data: rawGoSky, loading: goskyLoading, error: goskyError }, refetchGoSky] = useAxios(
    { url: withGoSky ? gosky.tripsUrl(selectedDate) : null },
    { useCache: false, manual: !withGoSky },
  );
  const refetchAll = async () => {
    await Promise.all([refetch(), withGoSky ? refetchGoSky() : null]);
  };

  useEffect(() => {
    const handleOnline = async () => {
      setIsOffline(false);
      setTimeout(async () => {
        try {
          await refetchAll();
        } catch (error) {
          console.error('Error refetching data on reconnect:', error);
        }
      }, 2000);
    };

    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, refetchGoSky, withGoSky]);

  useEffect(() => {
    const timer = setTimeout(() => refetchAll().catch(() => {}), 300000);
    return () => clearTimeout(timer);
  });

  if (loading || (withGoSky && goskyLoading))
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
        }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );

  const reloadData = () => {
    setSpinner(true);

    setTimeout(() => {
      refetchAll().catch(() => {});
      setSpinner(false);
    }, 1000);
  };

  // axios-hooks keeps the last good body after a failure, so a failed side
  // contributes nothing rather than yesterday's trips.
  const gojumpTrips = error ? [] : asList(rawGoJump);
  const goskyTrips = withGoSky && !goskyError ? asList(rawGoSky) : [];
  const getTrips = mergeTrips(gojumpTrips, goskyTrips);

  let errorMsg = '';
  if (isOffline)
    errorMsg =
      "You're offline. Please check your internet connection. Data will automatically refresh once you're back online.";
  var isNetworkError = false;
  if (error && goskyTrips.length === 0) {
    const status = error?.response?.status;
    isNetworkError = error.message === 'Network Error';

    if (status === 404) {
      errorMsg = 'No programmed availability for this date. Please check the desktop version for updates.';
    } else if (isNetworkError) {
      errorMsg =
        "You're offline. Please check your internet connection. Data will automatically refresh once you're back online.";
    } else {
      errorMsg = 'Something went wrong. Please try again later.';
    }
  }

  if (errorMsg)
    return (
      <div className="px-3 py-3 ">
        <div className="px-3 py-3 d-flex justify-content-center">{errorMsg}</div>
        {(isOffline || isNetworkError) && !spinner && (
          <div className="d-flex justify-content-center">
            <button className="btn btn-primary " onClick={() => reloadData()}>
              Reload
            </button>
          </div>
        )}
        {spinner && (
          <div className="d-flex justify-content-center">
            <Spinner animation="border" variant="primary" />
          </div>
        )}
      </div>
    );

  // One side failed while the other answered: show what we have and say so.
  let noticeMsg = '';
  if (withGoSky && goskyError) noticeMsg = "GoSky Hawaii trips couldn't be loaded. GoJump trips are shown.";
  else if (error && error?.response?.status !== 404) noticeMsg = "GoJump trips couldn't be loaded. GoSky Hawaii trips are shown.";

  const handleClick = (trip) => {
    history.push(tripDetailsPath(selectedDate, LocationId, trip));
  };

  const groupTripsByHour = (trips) => {
    return trips.reduce((groups, trip) => {
      const hour = trip.pickupTime.hour;
      if (!groups[hour]) groups[hour] = [];
      groups[hour].push(trip);
      return groups;
    }, {});
  };

  const byTime = (a, b) =>
    a.pickupTime.minute - b.pickupTime.minute || String(a.pickupSpot).localeCompare(String(b.pickupSpot));

  const incompleteTrips = getTrips.filter((trip) => trip.tripStatus === 0);
  const completeTrips = getTrips.filter((trip) => trip.tripStatus === 1);

  const groupedIncomplete = groupTripsByHour(incompleteTrips);
  const groupedComplete = groupTripsByHour(completeTrips);

  const sortedIncompleteHours = Object.keys(groupedIncomplete)
    .map(Number)
    .sort((a, b) => a - b);
  const sortedCompleteHours = Object.keys(groupedComplete)
    .map(Number)
    .sort((a, b) => a - b);

  const renderHours = (hours, grouped) =>
    hours.map((hour) => (
      <div key={hour} style={{ paddingTop: '1rem' }}>
        <div className="hour-header" style={{ padding: '0.5rem', fontWeight: 'bold' }}>
          {hour.toString().padStart(2, '0')}:00
        </div>
        <div
          className="hour-group"
          style={{
            marginBottom: '1rem',
            borderRadius: '5px',
            border: '1px solid #ddd',
            borderLeft: '4px solid blue',
            overflow: 'hidden',
          }}>
          {grouped[hour]
            .slice()
            .sort(byTime)
            .map((trip, idx) => (
              <Row
                key={idx}
                className="list-item"
                onClick={() => handleClick(trip)}
                style={{
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderTop: idx === 0 ? 'none' : '1px solid #eee',
                }}>
                <Col xs={10}>
                  <div style={{ padding: '1rem' }}>
                    <div className="title">{trip.pickupSpot.replace(/\s*\(Return\)/i, '')}</div>
                    <div className="subtitle">
                      <span style={{ fontWeight: 600, color: '#636363' }}>
                        {String(trip.pickupTime.hour).padStart(2, '0')}:
                        {String(trip.pickupTime.minute).padStart(2, '0')}
                      </span>{' '}
                      • <span>{trip.numberOfPeople} people</span>
                    </div>
                  </div>
                </Col>
                <Col xs={2} className="text-right pr-10 my-auto">
                  <IconContext.Provider value={{ color: '#1D0D37', size: '1.2em' }}>
                    <IoIosArrowForward />
                  </IconContext.Provider>
                </Col>
              </Row>
            ))}
        </div>
      </div>
    ));

  return (
    <div>
      <Container className="mt-3 main-wrapper" id="trip-list" style={{ border: 'none', paddingBottom: '5rem' }}>
        {noticeMsg && (
          <div className="subtitle-dark d-flex justify-content-between align-items-center px-2 pt-2">
            <span>{noticeMsg}</span>
            {spinner ? (
              <Spinner animation="border" variant="primary" size="sm" />
            ) : (
              <button className="btn btn-sm btn-outline-primary ml-2" onClick={() => reloadData()}>
                Reload
              </button>
            )}
          </div>
        )}
        <Tabs defaultActiveKey="incomplete" id="uncontrolled-tab-example">
          <Tab eventKey="incomplete" title="Incomplete">
            <div className="list-wrapper">{renderHours(sortedIncompleteHours, groupedIncomplete)}</div>
          </Tab>
          <Tab eventKey="complete" title="Complete">
            <div className="list-wrapper">{renderHours(sortedCompleteHours, groupedComplete)}</div>
          </Tab>
        </Tabs>
      </Container>

      {(roles.includes('Manager') || roles.includes('Super Admin')) && (
        <div className="bottom-menu d-flex px-1" style={{ gap: '0.1rem' }}>
          <button
            className={`btn button-large-third flex-grow-1 ${LocationId === 1 ? 'button-large-third--active' : ''}`}
            onClick={() => updateData(1)}>
            Las Vegas
          </button>
          <button
            className={`btn button-large-third flex-grow-1 ${LocationId === 5 ? 'button-large-third--active' : ''}`}
            onClick={() => updateData(5)}>
            Hawaii
          </button>
        </div>
      )}
    </div>
  );
}

export default TripsList;
